'use client';

import {
  Accordion,
  AccordionConsumer,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Container,
  FadingGradient,
} from 'components/shared';
import { H2, P } from './StyleConstants';
import { useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import gsap from 'gsap';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import type { LabTestGroup, SectionTheme } from './types/bloodVision';
import styles from './LabTestAccordionSection.module.css';

const cx = (...classes: (string | undefined)[]) =>
  classes.filter(Boolean).join(' ');

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const LabTestsData = [
  {
    title: 'Heart Health',
    subtitle: '15 Biomarkers',
    biomarkerTags: [
      'Apolipoprotein B (ApoB)',
      'High-Sensitivity C-Reactive Protein (hs-CRP)',
      'Lipoprotein (a)',
      'HDL Cholesterol',
      'Non-HDL Cholesterol',
      'Triglycerides',
      'Total Cholesterol / HDL Ratio',
      'Total Cholesterol',
      'LDL Cholesterol',
      'LDL Medium',
      'LDL Pattern',
      'LDL Small',
      'HDL Large',
      'LDL Particle Number',
      'LDL Peak Size',
    ],
  },
  {
    title: 'Thyroid Profile',
    subtitle: '5 Biomarkers',
    biomarkerTags: [
      'Thyroglobulin Antibodies',
      'Thyroid Peroxidase Antibodies',
      'Thyroid-Stimulating Hormone',
      'Triiodothyronine (T3) Free',
      'Thyroxine (T4) Free',
    ],
  },
  {
    title: 'Autoimmune Panel',
    subtitle: '5 Biomarkers',
    biomarkerTags: [
      'Antinuclear Antibodies Pattern',
      'Antinuclear Antibodies Titer',
      'Antinuclear Antibodies Screen',
      'Rheumatoid Factor',
      'Thyroid Peroxidase Antibodies',
    ],
  },
  {
    title: 'Immune Regulation',
    subtitle: '7 Biomarkers',
    biomarkerTags: [
      'Basophils',
      'Lymphocytes',
      'Neutrophils',
      'Eosinophils',
      'Monocytes',
      'White Blood Cell Count',
      'High-Sensitivity C-Reactive Protein (hs-CRP)',
    ],
  },
  {
    title: 'Male Health',
    subtitle: '7 Biomarkers',
    biomarkerTags: [
      'Testosterone, Free',
      'Testosterone, Total',
      'Estradiol',
      'Follicle Stimulating Hormone',
      'Luteinizing Hormone',
      'Prolactin',
      'Sex Hormone Binding Globulin',
    ],
  },
  {
    title: 'Female Health',
    subtitle: '9 Biomarkers',
    biomarkerTags: [
      'Anti-Mullerian Hormone',
      'Sex Hormone Binding Globulin',
      'Follicle Stimulating Hormone',
      'Prolactin',
      'DHEA–Sulfate',
      'Testosterone, Free',
      'Luteinizing Hormone',
      'Estradiol',
      'Testosterone, Total',
    ],
  },
  {
    title: 'Diabetes Panel',
    subtitle: '4 Biomarkers',
    biomarkerTags: ['Insulin', 'Glucose', 'Leptin', 'Hemoglobin A1c (HbA1c)'],
  },
  {
    title: 'Nutrient Panel',
    subtitle: '16 Biomarkers',
    biomarkerTags: [
      'Arachidonic Acid/EPA Ratio',
      'Omega-3 Total',
      'Omega-6: Linoleic Acid',
      'Omega-6 Total',
      'Omega-6: Arachidonic Acid',
      'Omega-6 / Omega-3 Ratio',
      'Ferritin',
      'Iron Binding Capacity',
      'Iron',
      'Iron % Saturation',
      'Magnesium',
      'Vitamin D',
      'Calcium',
      'Homocysteine',
      'Methylmalonic Acid',
      'Zinc',
    ],
  },
  {
    title: 'Stress & Ageing Markers',
    subtitle: '3 Biomarkers',
    biomarkerTags: ['Biological Age', 'Cortisol', 'DHEA-Sulfate'],
  },
  {
    title: 'Liver Function',
    subtitle: '8 Biomarkers',
    biomarkerTags: [
      'Alanine Transaminase',
      'Alkaline Phosphatase',
      'Gamma-glutamyl Transferase',
      'Total Protein',
      'Albumin',
      'Aspartate Aminotransferase',
      'Total Bilirubin',
      'Globulin',
    ],
  },
  {
    title: 'Kidney Function',
    subtitle: '9 Biomarkers',
    biomarkerTags: [
      'Blood Urea Nitrogen',
      'Serum Creatinine',
      'BUN/Creatinine Ratio',
      'eGFR',
      'Sodium',
      'Potassium',
      'Chloride',
      'Carbon Dioxide',
      'Uric Acid',
    ],
  },
  {
    title: 'Pancreas Function',
    subtitle: '2 Biomarkers',
    biomarkerTags: ['Amylase', 'Lipase'],
  },
  {
    title: 'Heavy Metal Markers',
    subtitle: '2 Biomarkers',
    biomarkerTags: ['Lead', 'Mercury'],
  },
  {
    title: 'Complete Blood Count',
    subtitle: '10 Markers',
    biomarkerTags: [
      'Mean Corpuscular Hemoglobin (MCH)',
      'Mean Platelet Volume (MPV)',
      'Red Blood Cell Count',
      'Mean Corpuscular Hemoglobin Concentration (MCHC)',
      'Mean Corpuscular Volume (MCV)',
      'Platelet Count',
      'Red Cell Distribution Width (RDW)',
      'Hemoglobin',
      'Hematocrit',
      'ABO Group and Rhesus (Rh) Factor',
    ],
  },
  {
    title: 'Electrolyte Panel',
    subtitle: '6 Biomarkers',
    biomarkerTags: [
      'Choride',
      'Potassium',
      'Sodium',
      'Carbon Dioxide',
      'Calcium',
      'Magnesium',
    ],
  },
  {
    title: 'Urine Panel',
    subtitle: '15 Biomarkers',
    biomarkerTags: [
      'Albumin, Urine',
      'Bilirubin',
      'Glucose, Urine',
      'Leukocytes',
      'Occult Blood, Urine',
      'Protein, Urine',
      'Granular Casts',
      'Red Blood Cell, Urine',
      'Appearance, Urine',
      'Color, Urine',
      'Ketones, Urine',
      'Nitrite, Urine',
      'pH, Urine',
      'Specific Gravity, Urine',
      'White Blood Cell, Urine',
    ],
  },
];

interface LabTestAccordionSectionProps {
  className?: string;
  htmlId?: string;
  /** API-driven tests (partner plans); defaults to the static list. */
  labTests?: LabTestGroup[];
  headerTitle?: string;
  headerSubtitle?: string;
  theme?: SectionTheme;
}

export const LabTestAccordionSection = ({
  className,
  htmlId,
  labTests,
  headerTitle,
  headerSubtitle,
  theme = 'light',
}: LabTestAccordionSectionProps) => {
  const data = labTests
    ? labTests.map((item) => ({
        title: item.title,
        subtitle: item.subtitle,
        biomarkerTags: item.list.map((l) => l.title),
      }))
    : LabTestsData;
  const leftItems = data.slice(0, Math.ceil(data.length / 2));
  const rightItems = data.slice(Math.ceil(data.length / 2));

  const accordionLeftRefs = useRef<(HTMLDivElement | null)[]>([]);
  accordionLeftRefs.current = []; // reset before rendering
  const accordionRightRefs = useRef<(HTMLDivElement | null)[]>([]);
  accordionRightRefs.current = []; // reset before rendering

  const { width } = useWindowSize();

  useIsomorphicLayoutEffect(() => {
    const allRefs = [
      ...accordionLeftRefs.current,
      ...accordionRightRefs.current,
    ];
    allRefs.forEach((el) => {
      if (el) {
        gsap.fromTo(
          el,
          { autoAlpha: 0, y: 30 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.3,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: el,
              start: 'top 75%',
              toggleActions: 'play none none none',
            },
          },
        );
      }
    });

    // Refresh ScrollTrigger to ensure animations are applied on page load
    ScrollTrigger.refresh();

    return () => {
      allRefs.forEach((el) => {
        if (el) {
          gsap.killTweensOf(el);
        }
      });
    };
  }, [width]);

  return (
    <section
      className={cx(styles.root, className)}
      id={htmlId}
      data-theme={theme}
    >
      <Container>
        <FadingGradient color={theme === 'dark' ? '#000000' : '#f7f7f7'}>
          <H2 className={styles.heading}>
            {headerTitle || 'One dashboard. 100+ biomarkers.'}
          </H2>
          <P className={styles.para}>
            {headerSubtitle ||
              'The following tests are included in your $499 Ultrahuman Annual Membership.*'}
          </P>

          <Accordion type="single" collapsible defaultValue="l0">
            <div className={styles.accordionLayout}>
              <div className={styles.column}>
                {leftItems.map((item, index) => (
                  <AccordionItem
                    key={index}
                    value={'l' + index.toString()}
                    ref={(el) => {
                      accordionLeftRefs.current[index] = el;
                    }}
                  >
                    <AccordionTrigger>
                      <div>
                        <h5 className={styles.title}>{item.title}</h5>
                        <p className={styles.subtitle}>{item.subtitle}</p>
                      </div>
                    </AccordionTrigger>
                    <AccordionConsumer>
                      {(d) =>
                        d?.value === 'l' + index.toString() ? (
                          <hr className={styles.separator} />
                        ) : null
                      }
                    </AccordionConsumer>
                    <AccordionContent>
                      <div className={styles.chipContainer}>
                        {item.biomarkerTags.map((tag, index) => (
                          <span
                            key={index}
                            className={cx(styles.chip, 'biomarker-tag')}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </div>
              <div className={styles.column}>
                {rightItems.map((item, index) => (
                  <AccordionItem
                    key={index}
                    value={'r' + index.toString()}
                    ref={(el) => {
                      accordionRightRefs.current[index] = el;
                    }}
                  >
                    <AccordionTrigger>
                      <div>
                        <h5 className={styles.title}>{item.title}</h5>
                        <p className={styles.subtitle}>{item.subtitle}</p>
                      </div>
                    </AccordionTrigger>
                    <AccordionConsumer>
                      {(d) =>
                        d?.value === 'r' + index.toString() ? (
                          <hr className={styles.separator} />
                        ) : null
                      }
                    </AccordionConsumer>
                    <AccordionContent>
                      <div className={styles.chipContainer}>
                        {item.biomarkerTags.map((tag, index) => (
                          <span
                            key={index}
                            className={cx(styles.chip, 'biomarker-tag')}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </div>
            </div>
          </Accordion>
        </FadingGradient>
      </Container>
    </section>
  );
};
